import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import DataTable from 'react-data-table-component';
import { MdLocationCity, MdAdd, MdEdit, MdDelete, MdRefresh, MdStar } from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import CountrySelect from '../../../shared/layout/forms/CountrySelect';
import {
    fetchAdminCities,
    fetchAdminCityStatistics,
    createAdminCity,
    updateAdminCity,
    deleteAdminCity
} from '../../../services/Http/AdminApi';
import { fetchCountryCodes } from '../../../services/Http/Api';

const AdminCities = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [cities, setCities] = useState([]);
    const [countries, setCountries] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [capitalFilter, setCapitalFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalRows, setTotalRows] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(defaultForm());
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    useEffect(() => {
        loadCountries();
        loadStatistics();
    }, []);

    useEffect(() => {
        loadCities();
    }, [currentPage, perPage, search, countryFilter, capitalFilter]);

    const loadCountries = async () => {
        try {
            const data = await fetchCountryCodes();
            setCountries(Array.isArray(data) ? data : []);
        } catch (err) {
            // non-blocking
        }
    };

    const loadStatistics = async () => {
        try {
            const resp = await fetchAdminCityStatistics();
            setStatistics(resp?.data || resp);
        } catch (err) {
            // non-blocking
        }
    };

    const loadCities = async () => {
        try {
            setLoading(true);
            const resp = await fetchAdminCities({
                per_page: perPage,
                page: currentPage,
                search,
                country_id: countryFilter,
                is_capital: capitalFilter,
            });
            const data = resp?.data || resp || [];
            setCities(data);
            setTotalRows(resp?.meta?.total ?? data.length);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                latitude: parseFloat(form.latitude),
                longitude: parseFloat(form.longitude),
                population: form.population ? parseInt(form.population, 10) : null,
                is_capital: !!form.is_capital,
            };

            if (editingId) {
                await updateAdminCity(editingId, payload);
            } else {
                await createAdminCity(payload);
            }
            resetForm();
            setShowModal(false);
            await loadCities();
            await loadStatistics();
        } catch (err) {
            setError(err);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteAdminCity(id);
            setDeleteConfirm(null);
            await loadCities();
            await loadStatistics();
        } catch (err) {
            setError(err);
        }
    };

    const columns = useMemo(() => [
        {
            name: t('AdminCities.columns.name'),
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <CityName>
                    {row.is_capital && <MdStar color="#f59e0b" size={16} />}
                    <span>{row.name}</span>
                </CityName>
            ),
        },
        {
            name: t('AdminCities.columns.country'),
            selector: (row) => row.country_name || countryLabel(row.country_id, countries),
        },
        {
            name: t('AdminCities.columns.coordinates'),
            selector: (row) => `${row.latitude?.toFixed(4)}, ${row.longitude?.toFixed(4)}`,
        },
        {
            name: t('AdminCities.columns.population'),
            selector: (row) => row.population ? row.population.toLocaleString() : '-',
            sortable: true,
        },
        {
            name: t('AdminCities.columns.capital'),
            selector: (row) => (row.is_capital ? t('Common.yes') : t('Common.no')),
            sortable: true,
        },
        {
            name: t('AdminCities.columns.actions'),
            cell: (row) => (
                <InlineActions>
                    <IconButton type="button" onClick={() => handleEdit(row)} title={t('Common.edit')}>
                        <MdEdit size={18} />
                    </IconButton>
                    <IconButton type="button" onClick={() => setDeleteConfirm(row)} title={t('Common.delete')} $danger>
                        <MdDelete size={18} />
                    </IconButton>
                </InlineActions>
            ),
            ignoreRowClick: true,
        },
    ], [countries, t]);

    const handleEdit = (city) => {
        setEditingId(city.id);
        setForm({
            name: city.name || '',
            country_id: city.country_id || '',
            latitude: city.latitude?.toString() || '',
            longitude: city.longitude?.toString() || '',
            region: city.region || '',
            population: city.population?.toString() || '',
            is_capital: city.is_capital || false,
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setEditingId(null);
        setForm(defaultForm());
    };

    const tableStyles = useMemo(() => getAdminTableStyles(theme), [theme]);

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('AdminCities.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                <StatsGrid>
                    <StatCard
                        icon={<MdLocationCity size={40} />}
                        title={t('AdminCities.stats.total')}
                        value={statistics?.total_cities?.toLocaleString() || '0'}
                        color="venues"
                    />
                    <StatCard
                        icon={<MdStar size={40} />}
                        title={t('AdminCities.stats.capitals')}
                        value={statistics?.capital_cities?.toLocaleString() || '0'}
                        color="warning"
                    />
                    <StatCard
                        icon={<MdLocationCity size={40} />}
                        title={t('AdminCities.stats.countries')}
                        value={statistics?.countries_with_cities?.toLocaleString() || '0'}
                        color="primary"
                    />
                </StatsGrid>

                <FiltersBar>
                    <SearchInput
                        placeholder={t('AdminCities.filters.searchPlaceholder')}
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                    />
                    <CountrySelect
                        countries={countries}
                        value={countryFilter}
                        onChange={(_, nextValue) => { setCountryFilter(nextValue || ''); setCurrentPage(1); }}
                        inputId="admin-cities-filter-country"
                        placeholder={t('AdminCities.filters.allCountries')}
                        menuPortalTarget={document.body}
                    />
                    <Select value={capitalFilter} onChange={(e) => { setCapitalFilter(e.target.value); setCurrentPage(1); }}>
                        <option value="">{t('AdminCities.filters.allCities')}</option>
                        <option value="true">{t('AdminCities.filters.capitalsOnly')}</option>
                        <option value="false">{t('AdminCities.filters.nonCapitals')}</option>
                    </Select>
                    <IconButton type="button" onClick={() => loadCities()} title={t('Common.refresh')}>
                        <MdRefresh size={18} />
                    </IconButton>
                    <PrimaryButton type="button" onClick={() => { resetForm(); setShowModal(true); }}>
                        <MdAdd size={18} />
                        {t('AdminCities.actions.add')}
                    </PrimaryButton>
                </FiltersBar>

                {error && <ErrorMessage>{t('AdminCities.errors.load')}</ErrorMessage>}
                <DataTable
                    columns={columns}
                    data={cities}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={(page) => setCurrentPage(page)}
                    onChangeRowsPerPage={(newPerPage, page) => {
                        setPerPage(newPerPage);
                        setCurrentPage(page);
                    }}
                    highlightOnHover
                    progressPending={loading}
                    progressComponent={<LoadingMessage>{t('AdminCities.loading')}</LoadingMessage>}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    persistTableHead
                    customStyles={tableStyles}
                    noDataComponent={t('AdminCities.empty')}
                />
            </PageContent>

            {showModal && (
                <ModalOverlay>
                    <ModalCard>
                        <ModalHeader>
                            <h4>{editingId ? t('AdminCities.modal.editTitle') : t('AdminCities.modal.addTitle')}</h4>
                            <IconButton type="button" onClick={() => { setShowModal(false); resetForm(); }}>
                                ✕
                            </IconButton>
                        </ModalHeader>
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                <Input
                                    name="name"
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder={t('AdminCities.form.name')}
                                    required
                                />
                                <CountrySelect
                                    countries={countries}
                                    value={form.country_id}
                                    onChange={(_, nextValue) => setForm((f) => ({ ...f, country_id: nextValue || '' }))}
                                    inputId="admin-city-country"
                                    placeholder={t('AdminCities.form.selectCountry')}
                                    menuPortalTarget={document.body}
                                />
                            </Row>
                            <Row>
                                <Input
                                    name="latitude"
                                    type="number"
                                    step="any"
                                    min="-90"
                                    max="90"
                                    value={form.latitude}
                                    onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                                    placeholder={t('AdminCities.form.latitude')}
                                    required
                                />
                                <Input
                                    name="longitude"
                                    type="number"
                                    step="any"
                                    min="-180"
                                    max="180"
                                    value={form.longitude}
                                    onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                                    placeholder={t('AdminCities.form.longitude')}
                                    required
                                />
                            </Row>
                            <Row>
                                <Input
                                    name="region"
                                    value={form.region}
                                    onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                                    placeholder={t('AdminCities.form.region')}
                                />
                                <Input
                                    name="population"
                                    type="number"
                                    min="0"
                                    value={form.population}
                                    onChange={(e) => setForm((f) => ({ ...f, population: e.target.value }))}
                                    placeholder={t('AdminCities.form.population')}
                                />
                            </Row>
                            <Row>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={!!form.is_capital}
                                        onChange={(e) => setForm((f) => ({ ...f, is_capital: e.target.checked }))}
                                    />
                                    <span style={{ marginLeft: 8 }}>{t('AdminCities.form.isCapital')}</span>
                                </label>
                            </Row>
                            <CoordinatesHint>
                                {t('AdminCities.form.coordinatesHint')}
                            </CoordinatesHint>
                            <Actions>
                                <PrimaryButton type="submit">
                                    {editingId ? t('Common.saveChanges') : t('AdminCities.actions.create')}
                                </PrimaryButton>
                                <SecondaryButton type="button" onClick={() => { setShowModal(false); resetForm(); }}>
                                    {t('Common.cancel')}
                                </SecondaryButton>
                            </Actions>
                        </Form>
                    </ModalCard>
                </ModalOverlay>
            )}

            {deleteConfirm && (
                <ModalOverlay>
                    <ModalCard style={{ maxWidth: '400px' }}>
                        <ModalHeader>
                            <h4>{t('AdminCities.modal.deleteTitle')}</h4>
                            <IconButton type="button" onClick={() => setDeleteConfirm(null)}>
                                ✕
                            </IconButton>
                        </ModalHeader>
                        <DeleteMessage>
                            {t('AdminCities.modal.deleteConfirm', { name: deleteConfirm.name })}
                        </DeleteMessage>
                        <Actions>
                            <DangerButton type="button" onClick={() => handleDelete(deleteConfirm.id)}>
                                {t('Common.delete')}
                            </DangerButton>
                            <SecondaryButton type="button" onClick={() => setDeleteConfirm(null)}>
                                {t('Common.cancel')}
                            </SecondaryButton>
                        </Actions>
                    </ModalCard>
                </ModalOverlay>
            )}
        </PageContainer>
    );
};

const countryLabel = (id, countries) => {
    const match = countries.find((c) => String(c.id || c.code) === String(id));
    return match ? match.name : '-';
};

const defaultForm = () => ({
    name: '',
    country_id: '',
    latitude: '',
    longitude: '',
    region: '',
    population: '',
    is_capital: false,
});

const CityName = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
`;

const FiltersBar = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.625rem;
    margin: 0.75rem 0;
`;

const SearchInput = styled.input`
    padding: 0.75rem 0.875rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    width: 100%;
    font-size: 0.95rem;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    transition: border 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
        outline: none;
    }
`;

const Select = styled(SimpleSelect)`
    padding: 0.75rem 0.875rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    width: 100%;
    font-size: 0.95rem;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    transition: border 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
        outline: none;
    }
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-weight: 700;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
`;

const SecondaryButton = styled.button`
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.textPrimary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s ease;

    &:hover {
        background: ${({ theme }) => theme.colors.surfaceMuted};
    }
`;

const DangerButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-weight: 700;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
`;

const InlineActions = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const IconButton = styled.button`
    background: ${({ theme, $danger }) => $danger ? 'rgba(239, 68, 68, 0.1)' : theme.colors.surfaceAlt};
    border: 1px solid ${({ theme, $danger }) => $danger ? 'rgba(239, 68, 68, 0.3)' : theme.colors.border};
    color: ${({ theme, $danger }) => $danger ? '#ef4444' : theme.colors.textPrimary};
    border-radius: 8px;
    padding: 0.375rem 0.5rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease, border 0.2s ease;

    &:hover {
        background: ${({ $danger }) => $danger ? 'rgba(239, 68, 68, 0.2)' : 'var(--color-primary-bg-soft)'};
        border-color: ${({ $danger }) => $danger ? '#ef4444' : 'var(--color-primary)'};
        color: ${({ $danger }) => $danger ? '#dc2626' : 'var(--color-primary)'};
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    z-index: 2000;
`;

const ModalCard = styled.div`
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 12px;
    padding: 1rem;
    width: min(700px, 100%);
    box-shadow: 0 10px 30px rgba(0,0,0,0.12);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.625rem;

    h4 {
        margin: 0;
        color: ${({ theme }) => theme.colors.textPrimary};
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.75rem;
`;

const Input = styled.input`
    padding: 0.75rem 0.875rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    width: 100%;
    font-size: 0.95rem;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    transition: border 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
        outline: none;
    }
`;

const Actions = styled.div`
    display: flex;
    gap: 0.625rem;
`;

const DeleteMessage = styled.p`
    color: ${({ theme }) => theme.colors.textPrimary};
    margin: 1rem 0;
`;

const CoordinatesHint = styled.p`
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.textMuted};
    margin: 0;
`;

const getAdminTableStyles = (theme) => ({
    headRow: {
        style: {
            backgroundColor: theme.colors.tableHeaderBg || '#f9fafb',
            border: `1px solid ${theme.colors.border}`,
            borderBottom: `1px solid ${theme.colors.border}`,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            minHeight: '48px',
        },
    },
    headCells: {
        style: {
            color: theme.colors.textMuted,
            fontSize: '12px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
        },
    },
    rows: {
        style: {
            backgroundColor: theme.colors.surface,
            color: theme.colors.textPrimary,
            borderLeft: `1px solid ${theme.colors.border}`,
            borderRight: `1px solid ${theme.colors.border}`,
            borderBottom: `1px solid ${theme.colors.border}`,
        },
        highlightOnHoverStyle: {
            backgroundColor: theme.colors.tableRowHoverBg || '#f8fafc',
            color: theme.colors.textPrimary,
        },
    },
    pagination: {
        style: {
            backgroundColor: theme.colors.surface,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.border}`,
            borderTop: 'none',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
        },
    },
});

export default AdminCities;
